<script setup>
import { reactive } from 'vue';
import { Collapse } from 'vue-collapsed'

import HowItWorksPhoto from "../../assets/images/other/sign-up-widget.png";

const questions = reactive([
  {
    title: 'Who are College Genius advisors, and how are they selected?',
    answer: 'Our advisors are current students at top universities—ranging from UCLA to Harvard—who have successfully navigated the admissions process. Each advisor is vetted through an application process and trained to provide strategic, school-specific support.',
    isExpanded: false
  },
  {
    title: 'What makes peer advising from real college students more effective than traditional consulting?',
    answer: 'College Genius connects you with students who were just in your shoes. They offer relatable, up-to-date insights on what worked for them—and what didn’t. You’ll get authentic, experience-based advice, not just generic tips.',
    isExpanded: false
  },
  {
    title: 'Can College Genius help me get into Ivy League or top 50 universities?',
    answer: 'Yes! Whether you\'re aiming for the Ivies, UC system, or top private institutions, we match you with advisors from those very schools. Our Premium Genius Advisors are Ivy League students who specialize in elite admissions strategy.',
    isExpanded: false
  },
  {
    title: 'What’s the difference between a Genius Advisor and a Premium Genius Advisor?',
    answer: 'Genius Advisor ($35/hr): Top 50 U.S. school students (e.g., NYU, USC, Michigan).\n\nPremium Genius Advisor ($55/hr): Ivy League or top-tier private school students (e.g., Harvard, Columbia). They typically have more advising experience and success stories.',
    isExpanded: false
  },
  {
    title: 'How do Zoom calls and essay feedback sessions work?',
    answer: 'You’ll schedule a call through your personalized dashboard. Before the session, you’ll complete an intake form. During the call, your advisor will guide you live, screen share, and even edit with you in real-time. Follow-up notes or resources are provided afterward.',
    isExpanded: false
  },
  {
    title: 'What services does College Genius offer for free?',
    answer: 'We occasionally offer free webinars, mini guides, and school-specific cheat sheets. You can also browse advisor profiles and reviews before booking—no signup fees or subscriptions required.',
    isExpanded: false
  },
  {
    title: 'How does College Genius differ from traditional admissions counselors or agencies?',
    answer: 'We’re peer-to-peer. That means real students, not generic consultants. You choose who you work with, see their school, background, and reviews up front, and only pay for what you need—no bloated package fees.',
    isExpanded: false
  },
  {
    title: 'Can College Genius help me with scholarships or financial aid advice?',
    answer: 'Yes! Many of our advisors received scholarships themselves and can walk you through finding and applying for merit-based or need-based aid, including tips for FAFSA and school-specific scholarships.',
    isExpanded: false
  },
  {
    title: 'How do I get matched with the right advisor for my goals?',
    answer: 'You can filter advisors by school, major, specialties (e.g., essay writing, pre-med, transfer apps), and even availability. Or, take our quick Advisor Match Quiz to get a shortlist tailored to your goals.',
    isExpanded: false
  },
  {
    title: 'Does College Genius support international applicants?',
    answer: 'Absolutely. Many of our advisors are international students or have helped others navigate U.S. admissions from abroad. We understand the unique challenges and paperwork involved.',
    isExpanded: false
  },
  {
    title: 'What if I’m not satisfied with the advisor I book?',
    answer: 'We have a satisfaction guarantee. If something wasn’t right, let us know within 24 hours and we’ll make it right—whether that’s a new advisor or a refund.',
    isExpanded: false
  },
  {
    title: 'What’s the success rate of students who use College Genius?',
    answer: 'Over 90% of students who complete more than 3 sessions with an advisor get accepted into one of their top 3 school choices. We track advisor impact through student feedback and admission outcomes.',
    isExpanded: false
  }
]);

function handleAccordion(selectedIndex) {
  questions.forEach((_, index) => {
    questions[index].isExpanded = index === selectedIndex ? !questions[index].isExpanded : false;
  });
}
</script>

<template>
  <div>

  
    <NavBar />

    <div class="page-wrapper">

      <div class="how-it-works">
        <div class="left-image">
          <img src="@/assets/images/how_it_works.png" alt="How it works screenshot" />
        </div>
        <div class="right-content">
          <section class="text-section">
            <h1 class="steps-title">Find Your Perfect College Mentor in 3 Simple Steps</h1>

            <div class="steps">
              <p>
                <strong>Step 1: Match with a Mentor from Your Dream School</strong>
                Use filters to search for mentors by school, subject, or price. Whether you’re looking for someone from your top-choice university or within your budget, we’ll help you find the right fit—someone who truly gets your goals and journey.
              </p>

              <p>
                <strong>Step 2: Schedule When It Works for You</strong>
                Once you’ve found your match, choose a time that fits your schedule. Our flexible booking system makes it easy to connect on your terms, whether it’s a one-time call or recurring sessions.
              </p>

              <p>
                <strong>Step 3: Build a Custom Learning Plan</strong>
                During your first session, your mentor will work with you to map out a personalized plan tailored to your admissions goals. From weekly check-ins to strategic prep, they’ll help you succeed.
              </p>
            </div>
          </section>
        </div>
      </div>

      <div class="faq-section">
        <h2 class="faq-title">FAQs</h2>
        <div class="faq-list">
          <div
            v-for="(question, index) in questions"
            :key="question.title"
            class="faq-card"
          >
            <button class="faq-toggle" @click="() => handleAccordion(index)">
              <span class="faq-question-text">{{ question.title }}</span>
              <span class="faq-icon">
                {{ question.isExpanded ? '▼' : '▶' }}
              </span>
            </button>
            <Collapse :when="question.isExpanded">
              <div class="faq-answer">
                {{ question.answer }}
              </div>
            </Collapse>
          </div>
        </div>
      </div>

      <div class="how-it-works__widget">
          <picture class="how-it-works__widget-picture">
            <img :src="HowItWorksPhoto" alt="How It Works Widget" />
          </picture>
          <div class="how-it-works__widget-content">
              <h1 class="how-it-works__widget-title">
                  Get Started
              </h1>
              <div class="how-it-works__widget-description">
                  Have a question, suggestion or comment? Let us know how we can <br>
                  better support you on your wellness journey <br>
              </div>
              <div>
                  <button class="button-submit" @click="goToSignUp">Get Started</button>
              </div>
          </div>
      </div>
    </div>



  </div>
</template>

<script>
import NavBar from './../../components/NavBar.vue';

export default {
  components: {
    NavBar
  },
  methods: {
    goToSignUp() {
      this.$router.push('/auth/sign-up');
    }
  }
};
</script>

<style scoped>
.how-it-works {
  display: flex;
  margin-top: 100px; /* additional spacing to avoid navbar overlap */
}

.page-wrapper {
  background-color: white;
}

.left-image {
  width: 40%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: white;
  padding: 2rem;
}

.left-image img {
  max-width: 100%;
  height: auto;
  object-fit: contain;
}

.right-content {
  width: 55%;
  padding: 2rem;
  background-color: white;
}

.steps-title {
  font-size: 2rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 1.5rem;
  line-height: 1.4;
}

.steps p {
  font-size: 1.3rem;
  color: #333;
  line-height: 1.7;
  margin-bottom: 1.5rem;
}

.steps p strong {
  display: block;
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin-top: 2rem;
  margin-bottom: 0.5rem;
}

.hero-submit {
  background-color: #3867ae;
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.button-submit {
    background-color: #fcba03;
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 6px;
    font-size: 1rem;
    cursor: pointer;
}

.collapse {
  transition: height 600ms cubic-bezier(0.3, 0, 0.6, 1);
}

.collapse[data-collapse="expanded"] {
  display: block;
}

.faq-section {
  background-color: white;
  padding-bottom: 100px;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
}

.faq-title {
  text-align: center;
  font-size: 2.2rem;
  margin-bottom: 2rem;
  font-weight: 600;
}

.faq-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.faq-card {
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: box-shadow 0.2s ease-in-out;
}

.faq-toggle {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 1rem 1.25rem;
  font-size: 1.05rem;
  font-weight: 500;
  background-color: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
}

.faq-toggle:hover {
  background-color: #f0f4ff;
}

.faq-icon {
  font-size: 1.2rem;
  color: #333;
  margin-left: 1rem;
}

.faq-answer {
  padding: 1rem 1.25rem;
  background-color: #f9f9f9;
  border-top: 1px solid #e0e0e0;
  color: #333;
  font-size: 1.1rem;
  line-height: 1.6;
}

.faq-question-text {
  color: #000000;
  font-weight: 400;
  font-size: 1.2rem;
}

.faq-question-text::before {
  content: '•  ';
  margin-right: 0.5rem;
  color: #000;
  font-weight: bold;
}

.hero-submit:hover {
  background-color: #2d5392;
}
</style>